<template>
    <RegularModal ref="modal">
        <template v-slot:title>{{ translate("admin_starting_lists.title") }}</template>
        <template v-slot:body>
            <div v-if="store.startingListsSharing">
                <div class="buttons mb-4">
                    <a class="button" :href="eventAdmin().exportStartingListsToCsv(store.eventId)">
                        <span class="icon"><i class="fas fa-download"></i></span>
                        <span>{{ translate("admin_starting_lists.download") }}</span>
                    </a>
                </div>

                <p v-if="isShared" class="mb-4">
                    <span class="has-text-grey-light">{{ translate("admin_starting_lists.address") }}</span>
                    <br />
                    <a :href="store.startingListsSharing.publicUrl" target="_blank" rel="noopener">
                        {{ store.startingListsSharing.publicUrl }}
                    </a>
                </p>

                <div v-for="option in options" :key="option.sharingType" class="control" :class="{ 'mt-2': option.separated }">
                    <Radio name="startingListsSharingType" v-model="selected" :initial-value="option.sharingType" :disabled="saving">
                        {{ translate(option.translationKey) }}
                    </Radio>
                </div>

                <p v-if="isShared" class="help mt-4">{{ translate("admin_competitors.starting_lists_refresh_note") }}</p>
            </div>
        </template>
        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="close">{{ translate("common.cancel") }}</button>
                <LoadingButton :is-loading="saving" class="button is-primary" @click="save">
                    <span class="icon"><i class="fas fa-check"></i></span>
                    <span>{{ translate("common.save") }}</span>
                </LoadingButton>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { computed, ref } from "vue";
    import RegularModal from "../Common/RegularModal.vue";
    import Radio from "../Common/Radio.vue";
    import LoadingButton from "../Common/LoadingButton.vue";
    import { translate } from "../Common/translations";
    import { handleError } from "../Common/errorHandler";
    import { successToast } from "../Common/toasts";
    import { useModal } from "../Common/useModal";
    import urls from "../Urls/urls";
    import type { StartingListsSharingType } from "../Api/startingListsApi";
    import { useRegisteredCompetitorsStore } from "./registeredCompetitorsStore";

    const { eventAdmin } = urls;
    const store = useRegisteredCompetitorsStore();
    const { modal, open: openModal, close } = useModal();

    const options: { sharingType: StartingListsSharingType; translationKey: string; separated?: boolean }[] = [
        { sharingType: "ShowAllCompetitors", translationKey: "starting_list.show_all" },
        { sharingType: "ShowAllAndMarkDisqualified", translationKey: "starting_list.show_all_and_mark_disqualified" },
        { sharingType: "ShowNotDisqualified", translationKey: "starting_list.show_not_disqualified" },
        { sharingType: "ShowOnlyVerified", translationKey: "starting_list.show_only_verified" },
        { sharingType: "DisableStartingLists", translationKey: "starting_list.disabled", separated: true },
    ];

    const saving = ref(false);
    const selected = ref<StartingListsSharingType | undefined>(undefined);

    const isShared = computed(() => store.startingListsSharing?.sharingType !== "DisableStartingLists");

    function open() {
        selected.value = store.startingListsSharing?.sharingType;
        openModal();
    }

    function save() {
        if (!selected.value) {
            return;
        }

        saving.value = true;
        store
            .changeStartingListsSharingType(selected.value)
            .then(() => {
                successToast(translate("admin_competitors.starting_lists_changed_toast"));
                close();
            })
            .catch(handleError)
            .finally(() => {
                saving.value = false;
            });
    }

    defineExpose({ open });
</script>

<style scoped></style>
